import { apiClient } from '../../../shared/api/apiClient';

const LIST_PATH = '/message-templates/';
const FACETS_PATH = '/message-templates/facets/';
const FOR_TENANTS_PATH = '/message-templates/for-tenants/';
const VALIDATE_TRIGGERS_PATH = '/message-templates/validate-triggers/';

function appendTrimmed(params, key, value) {
  const trimmed = value != null ? String(value).trim() : '';
  if (trimmed.length > 0) {
    params.set(key, trimmed);
  }
}

function appendSharedFilters(params, { search, ordering, filters, tagsMode } = {}) {
  appendTrimmed(params, 'search', search);
  appendTrimmed(params, 'ordering', ordering);

  const status = filters?.status ?? [];
  const language = filters?.language ?? [];
  const type = filters?.type ?? [];
  const channels = filters?.channels ?? [];
  const tags = filters?.tags ?? [];
  const campaigns = filters?.campaigns ?? [];

  // One tag selection, sent as include (`tags`) or exclude (`tags_exclude`) per mode.
  const tagsParam = tagsMode === 'exclude' ? 'tags_exclude' : 'tags';

  for (const value of status) params.append('status', String(value));
  for (const value of language) params.append('language', String(value));
  for (const value of type) params.append('type', String(value));
  for (const value of channels) params.append('channel', String(value));
  for (const value of tags) params.append(tagsParam, String(value));
  for (const value of campaigns) params.append('campaigns', String(value));
}

function pathWithQuery(basePath, params) {
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

export function fetchMessageTemplatesList({
  page = 1,
  pageSize,
  search,
  ordering,
  filters,
  tagsMode,
} = {}) {
  const params = new URLSearchParams();
  if (page != null) params.set('page', String(page));
  if (pageSize != null) params.set('page_size', String(pageSize));
  appendSharedFilters(params, {
    search,
    ordering,
    filters,
    tagsMode,
  });
  return apiClient.get(pathWithQuery(LIST_PATH, params));
}

export function fetchMessageTemplatesFacets({ search, ordering, filters, tagsMode } = {}) {
  const params = new URLSearchParams();
  appendSharedFilters(params, {
    search,
    ordering,
    filters,
    tagsMode,
  });
  return apiClient.get(pathWithQuery(FACETS_PATH, params));
}

export function createMessageTemplate(payload = {}) {
  return apiClient.postJson(LIST_PATH, payload);
}

export function createMessageTemplateForTenants(payload = {}) {
  return apiClient.postJson(FOR_TENANTS_PATH, payload);
}

export function fetchMessageTemplateById(templateId) {
  return apiClient.get(`${LIST_PATH}${templateId}/`);
}

export function updateMessageTemplate(templateId, payload = {}) {
  return apiClient.patchJson(`${LIST_PATH}${templateId}/`, payload);
}

export function setMessageTemplateStatus(templateId, status) {
  return apiClient.patchJson(`${LIST_PATH}${templateId}/`, {
    status,
  });
}

// Dry-run validation of trigger send-condition assignments (no template is saved).
// Returns 200 `{ valid, triggers }`; per-row errors are parsed in parseTriggerValidationErrors.
// `templateId` excludes the current template from cross-template slot uniqueness on edit.
export function validateTemplateTriggers({ triggers = [], templateId = null } = {}) {
  return apiClient.postJson(VALIDATE_TRIGGERS_PATH, {
    triggers,
    template_id: templateId,
  });
}
